import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '@/contexts/UserContext/UserContext';
import useAuthenticatedPage from './useAuthenticatedPage';
import useNonAuthenticatedPage from './useNonAuthenticatedPage';

export default function useAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, clearUser, user, isLoading: isUserLoading, error } = useUser();
  const { isAuthenticated, isLoading: isAuth0Loading, loginWithRedirect, logout, user: auth0User } = useAuth0();
  const hasNavigated = useRef(false);
  const navigationAttemptCount = useRef(0); // Track navigation attempts to prevent loops

  // The logIn function is used to set the user in context
  const logIn = useCallback(
    (auth0UserData: any) => {
      // Clear localStorage to force fresh data fetch
      localStorage.removeItem('user');
      clearUser();

      // Set basic user data, UserContext will fetch complete data
      const mappedUser: any = {
        id: auth0UserData.sub || '',
        name: auth0UserData.name || '',
        email: auth0UserData.email || '',
        thumbnail: auth0UserData.picture,
      };
      setUser(mappedUser);

      // Navigation will be handled by the useEffect based on user profile status
      // No immediate navigation here
    },
    [clearUser, setUser],
  );

  // Custom login function that forces account selection
  const loginWithGoogle = useCallback(async () => {
    await loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
        prompt: 'select_account', // This forces Google to show account selection
      },
    });
  }, [loginWithRedirect]);

  // The logOut function is used to remove the user from context
  const logOut = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('hasCompletedProfileSetup');
    hasNavigated.current = false;
    navigationAttemptCount.current = 0; // Reset navigation attempt counter
    clearUser(); // Clear user data from context
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
      // Note: federated logout should be configured in Auth0 dashboard
      // or handled through the logoutParams if supported by your Auth0 setup
    });
  }, [clearUser, logout]);

  // Handle navigation based on user profile completion
  useEffect(() => {
    if (error) {
      navigate('/backend-down');
      return;
    }

    // Prevent infinite navigation loops by limiting attempts
    if (navigationAttemptCount.current > 5) {

      return;
    }

    // Wait until both Auth0 and UserContext are done loading
    if (!isAuth0Loading && !isUserLoading && isAuthenticated && user && !hasNavigated.current) {
      const hasCompletedProfileSetup = localStorage.getItem('hasCompletedProfileSetup');

      // If setup is already marked as complete, go to links
      if (hasCompletedProfileSetup === 'true') {
        // But don't redirect if we're already in the setup flow
        const setupPaths = ['/profile-title', '/complete-profile', '/choose-template', '/add-links', '/preview-deploy'];
        if (setupPaths.includes(location.pathname)) {
          return;
        }

        if (location.pathname !== '/links') {
          hasNavigated.current = true;
          navigationAttemptCount.current += 1;
          navigate('/links');
        }
        return;
      }
      const isProfileComplete = user.title && user.profileComplete;
      const hasTemplate = user.template !== null && user.template !== undefined;

      // If user has completed all steps, redirect to links page and set the flag
      if (isProfileComplete && hasTemplate) {
        // Set the flag that the user has completed the full setup
        localStorage.setItem('hasCompletedProfileSetup', 'true');
        if (location.pathname !== '/links') {
          hasNavigated.current = true;
          navigationAttemptCount.current += 1;
          navigate('/links');
        }
        return;
      }

      // Determine the correct step in the flow based on user's progress
      let targetPath = '/links';

      if (!user.title) {
        targetPath = '/profile-title';
      } else if (!user.profileComplete) {
        targetPath = '/complete-profile';
      } else if (user.template === null || user.template === undefined) {
        // User has title and completed profile but no template selected
        targetPath = '/choose-template';
      } else {
        // User has completed all steps including template selection
        // Set the flag that the user has completed the full setup
        localStorage.setItem('hasCompletedProfileSetup', 'true');
        targetPath = '/links';
      }

      // Only navigate if we're not already at the target path
      // and we're not in the setup flow (let page components handle that)
      const setupPaths = ['/profile-title', '/complete-profile', '/choose-template', '/add-links', '/preview-deploy'];
      if (!setupPaths.includes(location.pathname) && location.pathname !== targetPath) {
        hasNavigated.current = true;
        navigationAttemptCount.current += 1;
        navigate(targetPath);
      }
    }
  }, [
    isAuthenticated,
    isAuth0Loading,
    isUserLoading,
    user,
    navigate,
    location.pathname,
    location.search,
    error,
    logIn,
  ]);

  // Reset navigation flag when user changes
  useEffect(() => {
    hasNavigated.current = false;
    navigationAttemptCount.current = 0; // Reset navigation attempt counter when user changes
  }, [user?.id]);

  // Handle the case where Auth0 session exists but user context is empty
  useEffect(() => {
    if (!isAuth0Loading && isAuthenticated && auth0User && !user) {
      logIn(auth0User);
    }
  }, [isAuthenticated, isAuth0Loading, auth0User, user, logIn]);

  // Return an object with authentication status and functions to log in and out.
  return {
    isAuthenticated: isAuthenticated && !isAuth0Loading,
    isLoading: isAuth0Loading || isUserLoading,
    logIn,
    logOut,
    useAuthenticatedPage,
    useNonAuthenticatedPage,
    loginWithRedirect, // Expose Auth0 login function
    loginWithGoogle, // Expose custom Google login function
  };
}